import React, { useState, useEffect } from 'react';
import { useApp } from '../../contexts/AppContext';

const EvaluationIndicators = () => {
  const { showSuccess, showError } = useApp();
  const [indicators, setIndicators] = useState([]);
  const [filteredIndicators, setFilteredIndicators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [selectedLevel, setSelectedLevel] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingIndicator, setEditingIndicator] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    level: '',
    type: ''
  });

  // Niveles educativos chilenos
  const educationalLevels = [
    '1° Básico', '2° Básico', '3° Básico', '4° Básico',
    '5° Básico', '6° Básico', '7° Básico', '8° Básico',
    '1° Medio', '2° Medio', '3° Medio', '4° Medio'
  ];

  // Tipos de evaluación
  const evaluationTypes = [
    { id: 'autoevaluacion', name: 'Autoevaluación' },
    { id: 'coevaluacion', name: 'Coevaluación' }
  ];

  useEffect(() => {
    loadIndicators();
  }, []);

  useEffect(() => {
    filterIndicators();
  }, [searchText, selectedLevel, selectedType, indicators]);

  const loadIndicators = async () => {
    try {
      setLoading(true);
      // Mock data - aquí se haría la llamada real a la API
      const mockIndicators = [
        {
          id: 1,
          title: 'Participación activa',
          description: 'Evalúa el nivel de participación del estudiante en las actividades del proyecto',
          level: '7° Básico',
          type: 'autoevaluacion',
          createdAt: new Date().toISOString()
        },
        {
          id: 2,
          title: 'Trabajo colaborativo',
          description: 'Evalúa la capacidad de trabajar en equipo y colaborar con otros estudiantes',
          level: '8° Básico',
          type: 'coevaluacion',
          createdAt: new Date().toISOString()
        },
        {
          id: 3,
          title: 'Responsabilidad',
          description: 'Evalúa el cumplimiento de plazos y compromisos adquiridos',
          level: '1° Medio',
          type: 'autoevaluacion',
          createdAt: new Date().toISOString()
        }
      ];

      setTimeout(() => {
        setIndicators(mockIndicators);
        setFilteredIndicators(mockIndicators);
        setLoading(false);
      }, 500);
    } catch (error) {
      showError('Error al cargar indicadores de evaluación');
      setLoading(false);
    }
  };

  const filterIndicators = () => {
    let filtered = [...indicators];

    // Filtrar por nivel
    if (selectedLevel) {
      filtered = filtered.filter(ind => ind.level === selectedLevel);
    }

    // Filtrar por tipo
    if (selectedType) {
      filtered = filtered.filter(ind => ind.type === selectedType);
    }

    // Filtrar por texto de búsqueda
    if (searchText.trim()) {
      const search = searchText.toLowerCase();
      filtered = filtered.filter(ind =>
        ind.title.toLowerCase().includes(search) ||
        ind.description.toLowerCase().includes(search) ||
        ind.level.toLowerCase().includes(search)
      );
    }

    setFilteredIndicators(filtered);
  };

  const handleOpenModal = (indicator = null) => {
    if (indicator) {
      setEditingIndicator(indicator);
      setFormData({
        title: indicator.title,
        description: indicator.description,
        level: indicator.level,
        type: indicator.type
      });
    } else {
      setEditingIndicator(null);
      setFormData({
        title: '',
        description: '',
        level: '',
        type: ''
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingIndicator(null);
    setFormData({
      title: '',
      description: '',
      level: '',
      type: ''
    });
  };

  const handleSaveIndicator = async () => {
    if (!formData.title.trim() || !formData.description.trim() || !formData.level || !formData.type) {
      showError('Todos los campos son requeridos');
      return;
    }

    try {
      // Aquí se enviaría a la API
      if (editingIndicator) {
        const updatedIndicators = indicators.map(ind =>
          ind.id === editingIndicator.id
            ? { ...ind, ...formData }
            : ind
        );
        setIndicators(updatedIndicators);
        showSuccess('Indicador actualizado correctamente');
      } else {
        const newIndicator = {
          id: Date.now(),
          ...formData,
          createdAt: new Date().toISOString()
        };
        setIndicators([newIndicator, ...indicators]);
        showSuccess('Indicador creado correctamente');
      }

      handleCloseModal();
    } catch (error) {
      showError('Error al guardar el indicador');
    }
  };

  const handleDeleteIndicator = async (indicatorId) => {
    if (window.confirm('¿Estás seguro de eliminar este indicador de evaluación?')) {
      try {
        // Aquí se enviaría a la API
        setIndicators(indicators.filter(ind => ind.id !== indicatorId));
        showSuccess('Indicador eliminado correctamente');
      } catch (error) {
        showError('Error al eliminar el indicador');
      }
    }
  };

  const handleClearFilters = () => {
    setSearchText('');
    setSelectedLevel('');
    setSelectedType('');
  };

  const getTypeName = (typeId) => {
    const type = evaluationTypes.find(t => t.id === typeId);
    return type ? type.name : typeId;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            Indicadores de Evaluación
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Gestiona los indicadores para autoevaluación y coevaluación de estudiantes
          </p>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="flex items-center space-x-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          <span>Nuevo Indicador</span>
        </button>
      </div>

      {/* Filtros */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Búsqueda por texto */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Buscar
            </label>
            <div className="relative">
              <input
                type="text"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Buscar por título, descripción o nivel..."
              />
              <svg className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>

          {/* Selector de nivel */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nivel
            </label>
            <select
              value={selectedLevel}
              onChange={(e) => setSelectedLevel(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">Todos los niveles</option>
              {educationalLevels.map((level) => (
                <option key={level} value={level}>
                  {level}
                </option>
              ))}
            </select>
          </div>

          {/* Selector de tipo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tipo
            </label>
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">Todos los tipos</option>
              {evaluationTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Botón para limpiar filtros */}
        {(searchText || selectedLevel || selectedType) && (
          <div className="mt-4">
            <button
              onClick={handleClearFilters}
              className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              <span>Limpiar filtros</span>
            </button>
          </div>
        )}
      </div>

      {/* Tabla de indicadores */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '25%' }}>
                  Título
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '45%' }}>
                  Descripción
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '12%' }}>
                  Nivel
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '13%' }}>
                  Tipo
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '5%' }}>
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredIndicators.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center">
                    <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    <p className="text-gray-500">No se encontraron indicadores de evaluación</p>
                    <p className="text-sm text-gray-400 mt-1">
                      {searchText || selectedLevel || selectedType ? 'Intenta cambiar los filtros' : 'Comienza agregando un nuevo indicador'}
                    </p>
                  </td>
                </tr>
              ) : (
                filteredIndicators.map((indicator) => (
                  <tr key={indicator.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      {indicator.title}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {indicator.description}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {indicator.level}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        indicator.type === 'autoevaluacion'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-purple-100 text-purple-800'
                      }`}>
                        {getTypeName(indicator.type)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex justify-center space-x-2">
                        <button
                          onClick={() => handleOpenModal(indicator)}
                          className="text-blue-600 hover:text-blue-800 p-2 rounded-lg hover:bg-blue-50 transition-colors"
                          title="Editar"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDeleteIndicator(indicator.id)}
                          className="text-red-600 hover:text-red-800 p-2 rounded-lg hover:bg-red-50 transition-colors"
                          title="Eliminar"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer con total de resultados */}
        {filteredIndicators.length > 0 && (
          <div className="px-6 py-3 bg-gray-50 border-t border-gray-200">
            <p className="text-sm text-gray-600">
              Mostrando <span className="font-medium">{filteredIndicators.length}</span> de <span className="font-medium">{indicators.length}</span> indicadores
            </p>
          </div>
        )}
      </div>

      {/* Modal para crear/editar indicador */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-900">
                {editingIndicator ? 'Editar Indicador de Evaluación' : 'Nuevo Indicador de Evaluación'}
              </h3>
            </div>

            <div className="p-6 space-y-6">
              {/* Título */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Título *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Ej: Participación activa en clases"
                />
              </div>

              {/* Descripción */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Descripción *
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Describe qué evalúa este indicador..."
                />
              </div>

              {/* Nivel y Tipo en la misma fila */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Nivel */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nivel *
                  </label>
                  <select
                    value={formData.level}
                    onChange={(e) => setFormData({ ...formData, level: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">Seleccionar nivel...</option>
                    {educationalLevels.map((level) => (
                      <option key={level} value={level}>
                        {level}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Tipo */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tipo *
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">Seleccionar tipo...</option>
                    {evaluationTypes.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end space-x-4">
              <button
                onClick={handleCloseModal}
                className="px-6 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveIndicator}
                disabled={!formData.title.trim() || !formData.description.trim() || !formData.level || !formData.type}
                className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {editingIndicator ? 'Guardar Cambios' : 'Crear Indicador'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EvaluationIndicators;
